import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BETA_MODE = process.env.REACT_APP_BETA_MODE === 'true';

/**
 * FeedbackButton — floating beta feedback widget.
 * Only visible when REACT_APP_BETA_MODE=true.
 * Bottom-left corner, stays above the nav bar.
 */
export default function FeedbackButton({ userId }) {
  const [open, setOpen]       = useState(false);
  const [text, setText]       = useState('');
  const [type, setType]       = useState('bug'); // 'bug' | 'idea' | 'other'
  const [sending, setSending] = useState(false);

  if (!BETA_MODE) return null;

  const send = async () => {
    if (!text.trim()) { toast.error('Write something first'); return; }
    setSending(true);
    try {
      // Store in backend (falls back to console if endpoint missing)
      await axios.post(`${API}/feedback`, {
        user_id: userId || 'anonymous',
        type,
        message: text.trim(),
        url: window.location.pathname,
        ts: new Date().toISOString(),
      }).catch(() => {
        // Endpoint may not exist yet — fall back to mailto
        window.location.href = `mailto:helena@hypseya.com?subject=HYPSEYA Beta Feedback [${type}]&body=${encodeURIComponent(text)}`;
      });
      toast.success('Feedback sent — thank you 🙏');
      setText('');
      setOpen(false);
    } finally {
      setSending(false);
    }
  };

  const typeLabels = { bug: '🐛 Bug', idea: '💡 Idea', other: '💬 Other' };

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Send feedback"
        style={{
          position: 'fixed',
          bottom: '82px', // above bottom nav
          left: '16px',
          zIndex: 900,
          width: '40px', height: '40px',
          borderRadius: '50%',
          background: 'rgba(30,107,104,0.12)',
          border: '1.5px solid rgba(30,107,104,0.3)',
          color: '#1E6B68',
          fontSize: '1.1rem',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 2px 12px rgba(30,107,104,0.15)',
          transition: 'all 0.2s ease',
        }}
      >
        {open ? '×' : '📝'}
      </button>

      {/* Panel */}
      {open && (
        <div style={{
          position: 'fixed',
          bottom: '132px',
          left: '16px',
          zIndex: 901,
          width: 'min(320px, calc(100vw - 32px))',
          background: 'rgba(255,255,255,0.97)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(30,107,104,0.18)',
          border: '1px solid rgba(30,107,104,0.15)',
          padding: '20px',
          backdropFilter: 'blur(12px)',
        }}>
          <p style={{ margin: '0 0 4px', fontWeight: '700', color: '#1E6B68', fontSize: '0.9rem' }}>
            Beta Feedback
          </p>
          <p style={{ margin: '0 0 14px', color: '#999', fontSize: '0.75rem' }}>
            Found a bug or have an idea? Tell us.
          </p>

          {/* Type selector */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
            {Object.entries(typeLabels).map(([k, v]) => (
              <button
                key={k}
                onClick={() => setType(k)}
                style={{
                  padding: '6px 10px', borderRadius: '20px', border: 'none',
                  background: type === k ? '#1E6B68' : 'rgba(30,107,104,0.08)',
                  color: type === k ? '#fff' : '#1E6B68',
                  fontSize: '0.72rem', fontWeight: '600', cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {v}
              </button>
            ))}
          </div>

          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={type === 'bug' ? 'What happened? What did you expect?' : type === 'idea' ? 'What would make HYPSEYA better?' : 'What\'s on your mind?'}
            rows={4}
            style={{
              width: '100%', padding: '10px 12px',
              borderRadius: '10px', border: '1.5px solid rgba(30,107,104,0.2)',
              fontSize: '0.88rem', lineHeight: 1.6, resize: 'none',
              fontFamily: 'inherit', boxSizing: 'border-box',
              outline: 'none', color: '#333',
            }}
          />
          <button
            onClick={send}
            disabled={sending || !text.trim()}
            style={{
              marginTop: '10px', width: '100%', padding: '12px',
              background: sending || !text.trim() ? '#ccc' : 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
              color: '#fff', border: 'none', borderRadius: '10px',
              fontSize: '0.88rem', fontWeight: '600',
              cursor: sending || !text.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      )}
    </>
  );
}
